// Offline detector — asymmetric hysteresis over probe outcomes (BL-1090).
//
// Asserting "offline" is expensive (a bidder who believes their feed is dead
// stops bidding), so it takes TWO consecutive failed probes. Clearing it is
// cheap and urgent, so it takes ONE success. That asymmetry is the direct
// answer to BUG-1126, where the banner could only clear on a browser `online`
// event that never fired — and therefore stuck forever.
//
// Pure and DOM-free on purpose: all timing is the caller's, so the thresholds
// and backoff schedule are unit-testable without fake DOM or a network.

import type { ProbeOutcome } from './reachability-probe';

/** Default consecutive failures required before asserting offline. */
export const DEFAULT_FAILURES_TO_ASSERT_OFFLINE = 2;

/**
 * Default recovery-probe backoff. Starts fast so a brief outage clears almost
 * immediately, then widens so a long outage doesn't hammer a server that may
 * itself be the thing struggling. The last entry is the steady-state cap.
 */
export const DEFAULT_BACKOFF_SCHEDULE_MS = [
    2_000, 4_000, 8_000, 16_000, 30_000,
] as const;

export interface OfflineDetectorOptions {
    /** Consecutive failures before `isOffline()` flips true. Default 2. */
    failuresToAssertOffline?: number;
    /** Recovery backoff steps in ms; the final entry is the cap. */
    backoffScheduleMs?: readonly number[];
}

export interface OfflineDetector {
    /** Current verdict. */
    isOffline(): boolean;
    /**
     * Record one probe outcome.
     *
     * @returns `true` when the verdict changed, so callers re-render only on a
     *   real transition rather than on every recovery tick.
     */
    record(outcome: ProbeOutcome): boolean;
    /** Delay before the next recovery probe, given failures observed so far. */
    nextDelayMs(): number;
    /**
     * Force back to healthy — used when a realtime channel re-subscribes,
     * which is stronger evidence the network works than any probe result.
     *
     * @returns `true` when the verdict changed.
     */
    reset(): boolean;
}

/**
 * Create an offline detector.
 *
 * @param options - {@link OfflineDetectorOptions}
 * @returns A stateful {@link OfflineDetector}. Not thread-safe; one per monitor.
 */
export function createOfflineDetector(
    options: OfflineDetectorOptions = {},
): OfflineDetector {
    const failuresToAssertOffline =
        options.failuresToAssertOffline ?? DEFAULT_FAILURES_TO_ASSERT_OFFLINE;
    const backoff = options.backoffScheduleMs ?? DEFAULT_BACKOFF_SCHEDULE_MS;

    let consecutiveFailures = 0;
    let offline = false;

    return {
        isOffline: () => offline,

        record(outcome) {
            if (outcome === 'reachable') {
                // One success is enough. Resetting the streak here is what
                // makes the failure count CONSECUTIVE rather than cumulative —
                // without it, a flaky-but-working connection would eventually
                // trip the bar permanently.
                consecutiveFailures = 0;
                const changed = offline;
                offline = false;
                return changed;
            }

            consecutiveFailures += 1;
            const shouldBeOffline =
                consecutiveFailures >= failuresToAssertOffline;
            const changed = shouldBeOffline !== offline;
            offline = shouldBeOffline;
            return changed;
        },

        nextDelayMs() {
            // First failure → backoff[0]. Past the end of the schedule we hold
            // at the last entry rather than growing without bound, so a
            // recovering buyer never waits longer than the cap.
            const index = Math.min(
                Math.max(consecutiveFailures - 1, 0),
                backoff.length - 1,
            );
            return backoff[index];
        },

        reset() {
            consecutiveFailures = 0;
            const changed = offline;
            offline = false;
            return changed;
        },
    };
}
